import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import jwtDecode from "jwt-decode";
import GetCookieByName from "./GetCookie";

// Mirrors useWhatsapp.js's Campaigns + Inbox sections closely — same query-key/
// invalidate-on-success shape. No Templates section (email has no Meta-style approval
// gate to manage a catalog for — a campaign stores its own subject/message directly) and
// no Opted-out section (that's WhatsApp-specific stop/unsubscribe language).

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

const fetchEmailCampaigns = async (userId) => {
  const { data } = await axios.get("/email-campaigns", { params: userId ? { userId } : {} });
  return data;
};

const fetchEmailCampaign = async (campaignId) => {
  const { data } = await axios.get(`/email-campaigns/${campaignId}`);
  return data;
};

const createEmailCampaign = async (payload) => {
  const { data } = await axios.post("/email-campaigns", payload);
  return data;
};

const runEmailCampaign = async (campaignId) => {
  const { data } = await axios.post(`/email-campaigns/${campaignId}/run`);
  return data;
};

const deleteEmailCampaign = async (campaignId) => {
  const { data } = await axios.delete(`/email-campaigns/${campaignId}`);
  return data;
};

// Polls while ANY campaign in the list has an in-flight run (queued/running) — runs are
// async (BullMQ), unlike WhatsApp's inline sends, so the list needs to auto-refresh for a
// Run button clicked from this same page to visibly progress without a manual reload.
// `userId` defaults to the caller's own campaigns server-side when omitted; pass another
// internal user's id (from useInternalUsers()) to view theirs via the same dropdown the
// Email Inbox uses.
export const useEmailCampaigns = (userId) =>
  useQuery({
    queryKey: ["email-campaigns", userId],
    queryFn: () => fetchEmailCampaigns(userId),
    staleTime: 30 * 1000,
    refetchInterval: (data) => {
      const anyInFlight = (data?.campaigns || []).some((c) => {
        const latest = c.runs?.[c.runs.length - 1];
        return latest && ["queued", "running"].includes(latest.status);
      });
      return anyInFlight ? 2000 : false;
    },
  });

// Polls while any run on the campaign is still queued/running — same "only poll while
// something's actually in flight" shape as usePlanSummary.js's useEmailLog before it, now
// pointed at the reusable campaign instead of a one-off log.
export const useEmailCampaign = (campaignId, { poll = false } = {}) =>
  useQuery({
    queryKey: ["email-campaign", campaignId],
    queryFn: () => fetchEmailCampaign(campaignId),
    enabled: Boolean(campaignId),
    refetchInterval: (data) => {
      if (!poll) return false;
      const runs = data?.campaign?.runs || [];
      const latest = runs[runs.length - 1];
      return latest && ["queued", "running"].includes(latest.status) ? 1500 : false;
    },
    staleTime: 0,
  });

export const useCreateEmailCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmailCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-campaigns"] }),
  });
};

export const useRunEmailCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runEmailCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-campaigns"] }),
  });
};

export const useDeleteEmailCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEmailCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-campaigns"] }),
  });
};

// ---------------------------------------------------------------------------
// Inbox — every email conversation across every plan, newest activity first. Distinct
// from usePlanSummary.js's usePlanEmailConversations (that one starts from a known
// planId); this is the admin-facing "what needs a reply right now" view.
// ---------------------------------------------------------------------------

// `userId` defaults to the caller's own conversations server-side when omitted (each staff
// member has their own index of sent emails/replies, since each sends via their own
// impersonated Gmail mailbox) — pass another internal user's id to view theirs instead.
// unreadOnly asks the server for just the unread threads (the creator's reply is the latest message and
// the mailbox owner hasn't opened the thread since) — it has to be server-side because the list is
// paginated. The response's `unreadCount` is always the mailbox's total unread, for the tab badge.
const fetchEmailInbox = async ({ page = 1, userId, unreadOnly = false, search = "" } = {}) => {
  const { data } = await axios.get("/email-admin/conversations", {
    params: { page, ...(userId ? { userId } : {}), ...(unreadOnly ? { unread: true } : {}), ...(search ? { search } : {}) },
  });
  return data;
};

export const useEmailInbox = (page = 1, userId, unreadOnly = false, search = "") =>
  useQuery({
    queryKey: ["email-inbox", page, userId, unreadOnly ? "unread" : "all", search],
    queryFn: () => fetchEmailInbox({ page, userId, unreadOnly, search }),
    staleTime: 15 * 1000,
    keepPreviousData: true,
  });

// AI follow-up on email replies (backend: helpers/emailAi.js). Settings are per MAILBOX — the AI replies from the
// address of the staff member whose mailbox the thread is in. The GET also says whether the caller may edit them.
export const useEmailAiSettings = (userId) =>
  useQuery({
    queryKey: ["email-ai-settings", userId],
    queryFn: async () => {
      const { data } = await axios.get(`/email-admin/ai-settings/${userId}`);
      return data;
    },
    enabled: Boolean(userId) && userId !== "all",
    staleTime: 30 * 1000,
    retry: false,
  });

export const useUpdateEmailAiSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, ...payload }) => {
      const { data } = await axios.patch(`/email-admin/ai-settings/${userId}`, payload);
      return data;
    },
    onSuccess: (_data, { userId }) => queryClient.invalidateQueries({ queryKey: ["email-ai-settings", userId] }),
  });
};

// Per-thread "Pause AI" switch in the Email Inbox.
export const useSetEmailAiPaused = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, paused }) => {
      const { data } = await axios.post(`/email-admin/conversations/${conversationId}/ai-pause`, { paused });
      return data;
    },
    onSuccess: (_data, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["email-admin-thread", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["email-inbox"] });
    },
  });
};

// The mailbox owner opened a thread (POST /email-admin/conversations/:id/read; the server refuses anyone
// else). Whatever the outcome, refetch the list and the thread so they show the server's truth.
export const useMarkEmailRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId) => {
      const { data } = await axios.post(`/email-admin/conversations/${conversationId}/read`);
      return data;
    },
    onSettled: (_data, _error, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ["email-inbox"] });
      queryClient.invalidateQueries({ queryKey: ["email-admin-thread", conversationId] });
    },
  });
};

const fetchEmailAdminThread = async (conversationId, userId) => {
  const { data } = await axios.get(`/email-admin/conversations/${conversationId}`, { params: userId ? { userId } : {} });
  return data;
};

export const useEmailAdminThread = (conversationId, userId) =>
  useQuery({
    queryKey: ["email-admin-thread", conversationId, userId],
    queryFn: () => fetchEmailAdminThread(conversationId, userId),
    enabled: Boolean(conversationId),
    staleTime: 10 * 1000,
  });

// ---------------------------------------------------------------------------
// Internal-user roster — the shared user-switcher dropdown on both the Email Inbox and
// Email Campaigns pages (each staff member sends/receives via their own mailbox, so both
// pages are per-user with a way to switch whose index you're viewing).
// ---------------------------------------------------------------------------

const fetchInternalUsers = async () => {
  const { data } = await axios.get("/email-admin/users");
  return data;
};

export const useInternalUsers = () =>
  useQuery({
    queryKey: ["email-internal-users"],
    queryFn: fetchInternalUsers,
    staleTime: 60 * 1000,
  });

// The logged-in user's own id, decoded from the auth cookie's JWT — same pattern as
// src/hooks/useAccess.js — used to default the Inbox/Campaigns user-switcher to yourself.
export const useCurrentUserId = () =>
  useMemo(() => {
    try {
      const token = JSON.parse(GetCookieByName("auth"))["token"];
      return jwtDecode(token)?._id || null;
    } catch (error) {
      return null;
    }
  }, []);

const sendEmailAdminReply = async ({ conversationId, message }) => {
  const { data } = await axios.post(`/email-admin/conversations/${conversationId}/reply`, { message });
  return data;
};

export const useSendEmailAdminReply = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendEmailAdminReply,
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["email-admin-thread", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["email-inbox"] });
    },
  });
};
